import { useQueryClient } from '@tanstack/react-query';
import { clearSupportSession, setSupportSession, useSupportSession } from '@my/platform/support';
import { useEffect } from 'react';
import { useSupportAccounts } from './useSupportAccounts';
import { usePermissions } from './usePermissions';
import type { SupportAccount } from '../client';

export interface SupportBarState {
  isSupportUser: boolean;
  accounts: SupportAccount[];
  currentAccountId: string;
  currentOrganisationId: string;
  isLoading: boolean;
  onSelect: (accountId: string, organisationId: string) => void;
  onExit: () => void;
  /**
   * Changes whenever the support session points somewhere new. Use it as a
   * `key` on the routed subtree — see the note on remounting below.
   */
  sessionKey: string;
}

/**
 * Everything `<SupportBar>` needs, in one call — so mounting it in an app is
 * `<SupportBar {...useSupportBar()} />` and the wiring exists once rather than
 * five times.
 *
 * <p>
 * Kept here, in the data package, rather than in `@my/ui`: that package is
 * presentational and depends on nothing, and the moment a component in it
 * fetches, every app pulls the API layer in through its button library.
 * </p>
 */
export interface SupportBarOptions {
  /**
   * How to sign out. Passed in rather than imported because this package has
   * no Auth0 dependency — the app owns the identity provider.
   */
  onSignOut?: () => void;
}

export function useSupportBar(options?: SupportBarOptions): SupportBarState {
  const queryClient = useQueryClient();
  const { session, enter, exit } = useSupportSession();

  /**
   * "Am I support?" is answered by `/me`, which every app already fetches for
   * its menu, rather than by asking for the account list and reading the 404.
   *
   * <p>
   * Still the server deciding — `role` is computed from the `super_user` claim
   * by `GetMeHandler`, not read off the token here. What changes is that the
   * answer now arrives as information instead of as a failed request: the
   * account list 404s for everybody who is not support, which is almost every
   * signed-in user, so every one of them logged a red console error on every
   * page load for a question that had already been answered.
   * </p>
   *
   * <p>
   * `roleKnown` is the guard that matters. A `/me` that is still in flight or
   * that failed tells us nothing, and "not support" must never be inferred
   * from "do not know yet" — see the session clear below.
   * </p>
   */
  const { role, isLoading: roleLoading, error: roleError } = usePermissions();
  const roleKnown = !roleLoading && !roleError && role !== null;
  const isSupportUser = roleKnown && role === 'SuperUser';

  // Only fetched once the role says it is worth fetching. Hub calls
  // `useSupportAccounts` directly and is unaffected — the gate is here, at the
  // one call site that runs inside a customer-facing app.
  const { data, isLoading: accountsLoading } = useSupportAccounts({
    enabled: isSupportUser,
  });
  const accounts = data ?? [];

  /**
   * SELF-HEAL. A support session lives in sessionStorage, which outlives a
   * sign-out on the same tab — so an ordinary user signing in afterwards
   * inherited one. Cosmetically that put the support label where their account
   * switcher belongs; substantively it left a stale organisation in
   * `getCurrentOrganisationId`, pointing their screens at another company's org.
   *
   * `roleKnown` is the signal, not `!isSupportUser`: the latter is also false
   * while `/me` is in flight or has failed, and clearing then would drop a
   * legitimate support session on every page load. Only a profile that LOADED
   * and says something other than SuperUser is the server telling us this
   * token is not support — at which point any session it carries is wreckage
   * from a previous one.
   *
   * This used to key off a 404 from the account list, which said the same
   * thing less directly and cost a failed request per page load to hear it.
   */
  useEffect(() => {
    if (roleKnown && !isSupportUser && session) clearSupportSession();
  }, [roleKnown, isSupportUser, session]);

  // A session handed over from My Hub arrives as ids only — names would be
  // stale the moment a customer is renamed. Fill them in once the list lands,
  // or the banner and sidebar label would read blank for the whole session.
  useEffect(() => {
    if (!session?.accountId || session.accountName) return;
    const account = accounts.find((a) => a.id === session.accountId);
    if (!account) return;

    const organisation = account.organisations.find((o) => o.id === session.organisationId);
    setSupportSession({
      ...session,
      accountName: account.name,
      organisationName: organisation?.name ?? '',
    });
  }, [session, accounts]);

  /**
   * Throw away every cached row and refetch what is on screen.
   *
   * `resetQueries`, NOT `clear`. `clear` empties the cache but leaves the
   * components that were watching it orphaned — they hold their last render
   * and never ask again, so the screen keeps showing the previous customer
   * until you navigate. `resetQueries` returns active queries to their initial
   * state AND refetches them, which is the behaviour anyone would expect from
   * "switch customer".
   *
   * Everything goes, deliberately: every list, detail and lookup in the app
   * belongs to the customer being left. Keeping any of it would render one
   * company's orders under another company's name in the banner.
   */
  const resetCaches = () => {
    void queryClient.resetQueries();
  };

  const onSelect = (accountId: string, organisationId: string) => {
    if (!accountId) {
      exit();
      resetCaches();
      return;
    }

    const account = accounts.find((a) => a.id === accountId);
    const organisation = account?.organisations.find((o) => o.id === organisationId);

    // Session first, caches second. The account travels as an `X-Account-Key`
    // header read at request time, so refetching before the session moves
    // would reload the customer we are leaving.
    enter({
      accountId,
      accountName: account?.name ?? '',
      organisationId,
      organisationName: organisation?.name ?? '',
    });
    resetCaches();
  };

  /**
   * Exit signs out. Switching between customers is what the dropdowns are
   * for, so the button's only remaining job is leaving entirely.
   *
   * Clear, then go — nothing else. No cache reset and no re-render: a
   * full-page redirect is about to discard all of it, and doing the work
   * anyway put a burst of refetches and a shell remount in the same tick as
   * the redirect.
   *
   * The clear still has to happen FIRST, and has to happen at all:
   * sessionStorage survives a redirect within the same tab, so leaving it
   * behind would drop the next person to sign in on this tab straight back
   * into the last customer's account.
   */
  const onExit = () => {
    clearSupportSession({ silent: true });
    options?.onSignOut?.();
  };

  return {
    isSupportUser,
    accounts,
    currentAccountId: session?.accountId ?? '',
    currentOrganisationId: session?.organisationId ?? '',
    // The bar is not ready until we know whether to draw it at all.
    isLoading: roleLoading || accountsLoading,
    onSelect,
    onExit,
    sessionKey: `${session?.accountId ?? ''}:${session?.organisationId ?? ''}`,
  };
}
